import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/shared/api/client';
import { qs } from '@/features/bi/shared/format';
import { ChartLoading, Failed } from '@/features/bi/shared/kpi';
import { IconTrendUp } from '@/components/ui/Icon';
import { registerWidget } from '../registry';
import {
  BI_CHARTS, BI_GROUP_ORDER, DEFAULT_BI_CHART, periodFilters, moneyFmt,
  type BiPeriod, type ChartBox,
} from './biChartCatalog';

type BiResp = { currency?: string } & Record<string, unknown>;

function resolve(config: Record<string, unknown>) {
  const def = BI_CHARTS[String(config.chart ?? DEFAULT_BI_CHART)] ?? BI_CHARTS[DEFAULT_BI_CHART];
  // Period (Year-to-date / Month-to-date) is offered on every chart so the
  // choice never disappears when switching charts; defaults to YTD.
  const period: BiPeriod = config.period === 'mtd' ? 'mtd' : 'ytd';
  return { def, period };
}

// Header title shown on the card: the chart's name + its period scope.
export function biChartTitle(config: Record<string, unknown>): string {
  const { def, period } = resolve(config);
  return `${def.label} · ${period.toUpperCase()}`;
}

// Track the chart area's real size so each chart is rendered to fit the tile —
// and re-fits live as the widget is dragged/resized on the canvas.
function useBoxSize(): [RefObject<HTMLDivElement>, ChartBox] {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<ChartBox>({ width: 320, height: 200 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setBox({ width: Math.round(cr.width), height: Math.round(cr.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, box];
}

function BiRender({ config }: { config: Record<string, unknown> }) {
  const { def, period } = resolve(config);
  const [boxRef, box] = useBoxSize();
  const q = useQuery({
    queryKey: ['home-bi', def.key, period],
    queryFn: () => api.get<BiResp>(`${def.endpoint}${qs(periodFilters(period))}`),
  });
  const M = moneyFmt(q.data?.currency);
  // The card header (WidgetCard) carries the chart title + period and the
  // "Open in Analytics" link (via headerAction), so the body is just the chart.
  return (
    <div ref={boxRef} className="h-full min-h-0">
      {q.isLoading
        ? <ChartLoading height="100%" />
        : q.data ? def.render(q.data, M, box)
        : <div className="h-full flex items-center justify-center"><Failed compact onRetry={() => void q.refetch()} /></div>}
    </div>
  );
}

// Header "Open in Analytics" link for the chosen chart's source tab.
function BiHeaderAction(config: Record<string, unknown>) {
  const { def } = resolve(config);
  return <Link to={def.path} className="inline-flex min-h-6 items-center rounded px-1.5 -mx-1.5 text-xs text-primary hover:underline">Open in Analytics</Link>;
}

function BiConfig({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const { def, period } = resolve(config);
  return (
    <div className="flex flex-col gap-1.5">
      <select
        className="h-[30px] px-2 rounded-md border border-border bg-surface text-[0.8125rem]"
        value={def.key}
        onChange={(e) => onChange({ ...config, chart: e.target.value })}
      >
        {BI_GROUP_ORDER.map((group) => {
          const items = Object.values(BI_CHARTS).filter((c) => c.group === group);
          return (
            <optgroup key={group} label={group}>
              {items.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </optgroup>
          );
        })}
      </select>
      <select
        className="h-[30px] px-2 rounded-md border border-border bg-surface text-[0.8125rem]"
        value={period}
        onChange={(e) => onChange({ ...config, period: e.target.value })}
      >
        <option value="ytd">Year to date</option>
        <option value="mtd">Month to date</option>
      </select>
    </div>
  );
}

registerWidget({
  type: 'bi_chart',
  label: 'BI chart',
  defaultSize: 'M',
  icon: <IconTrendUp size={13} />,
  accent: '#1f8ba3',
  title: biChartTitle,
  headerAction: BiHeaderAction,
  nodeFor: (c) => (BI_CHARTS[String(c.chart ?? DEFAULT_BI_CHART)] ?? BI_CHARTS[DEFAULT_BI_CHART]).node,
  ConfigForm: BiConfig,
  Render: BiRender,
});
